import { fetchWithAuth } from '../lib/fetchWithAuth';

const API_BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/$/, '');

export type EmployerJobStatus = 'draft' | 'pending_review' | 'approved' | 'rejected' | 'active';

export type EmployerJob = {
  id: number | string;
  title: string;
  location: string;
  type: string;
  salary: string;
  status: EmployerJobStatus;
  applicants: number;
  views: number;
  conversionRate?: number;
  lastViewAt?: string;
  lastApplyAt?: string;
  posted: string;
  description: string;
  companyId?: number;
  employerId?: string;
  occupationId?: string | null;
  occupationTitle?: string | null;
};

export type EmployerJobFilters = {
  status?: EmployerJobStatus | 'all';
};

const calculateConversionRate = (views?: number, applicants?: number) => {
  if (typeof views === 'number' && views > 0) {
    return Number((((applicants ?? 0) / views) * 100).toFixed(1));
  }
  if ((applicants ?? 0) > 0) {
    return 100;
  }
  return 0;
};

const normaliseJobsFromApi = (payload: unknown): EmployerJob[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as EmployerJob[];
  if (typeof payload === 'object') {
    if (Array.isArray((payload as { jobs?: unknown[] }).jobs)) {
      return (payload as { jobs: EmployerJob[] }).jobs;
    }
    if (Array.isArray((payload as { data?: unknown[] }).data)) {
      return (payload as { data: EmployerJob[] }).data;
    }
  }
  return [];
};

const enrichEmployerJob = (job: EmployerJob): EmployerJob => ({
  ...job,
  views: job.views ?? 0,
  conversionRate:
    typeof job.conversionRate === 'number'
      ? Number(job.conversionRate)
      : calculateConversionRate(job.views, job.applicants),
});

export async function fetchEmployerJobs(filters: EmployerJobFilters): Promise<EmployerJob[]> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to fetch employer jobs.');
  }

  const params = new URLSearchParams();
  if (filters.status) params.set('status', filters.status);

  const response = await fetchWithAuth(
    `${API_BASE_URL}/v1/employer/jobs${params.size ? `?${params.toString()}` : ''}`,
  );
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Employer jobs fetch failed (${response.status}): ${errorText}`);
  }
  const payload = await response.json();
  const jobs = normaliseJobsFromApi(payload?.jobs ?? payload);
  return jobs.map(enrichEmployerJob);
}

export async function updateEmployerJobStatus(id: string, status: EmployerJobStatus): Promise<EmployerJob> {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update employer jobs.');
  }
  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Job update failed (${response.status}): ${errorText}`);
  }
  return (await response.json()) as EmployerJob;
}

export async function bulkUpdateEmployerJobs(ids: string[], status: EmployerJobStatus) {
  if (!API_BASE_URL) {
    throw new Error('VITE_API_URL is required to update employer jobs.');
  }

  const response = await fetchWithAuth(`${API_BASE_URL}/v1/jobs/bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ids, status }),
  });
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'request failed');
    throw new Error(`Bulk update failed (${response.status}): ${errorText}`);
  }
  const payload = await response.json();
  return normaliseJobsFromApi(payload?.jobs) ?? [];
}
